import type {
  ProductBacklogCard as Card,
  ProductBacklogLockReason,
  ProductBacklogSectionKind,
} from '@cortex/domain-ac-mens-hub-api';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { GripVertical, Lock, MoreHorizontal } from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { backlogCellOf } from '../../lib/product-backlog-view.js';

/**
 * 札を動かす人の画面で札に渡すもの。動かす権限が無い人・見るだけの区分では渡さない（印もメニューも出さない）。
 * - movable: つかんで動かせる（つかむ印を出す）。false で lockReason があれば鍵の印と理由を出す
 * - menuTargets: 「…」の移動メニューに出す区分（空ならメニューを出さない）
 * - mark: 下書きで動かした札の印（「動かした 12→3」など）
 * - dropPosition: いま落とすとこの札の上（before）か下（after）に入る
 * - acceptsDrop: いまつかんでいる札をこの札の区分に落とせる
 * @graph-connects none
 */
export interface CardArrangeProps {
  movable: boolean;
  acceptsDrop: boolean;
  lockReason: ProductBacklogLockReason | null;
  menuTargets: readonly ProductBacklogSectionKind[];
  mark: string | null;
  flash: boolean;
  dropPosition: 'before' | 'after' | null;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (position: 'before' | 'after') => void;
  onDrop: () => void;
  onMenuMove: (to: ProductBacklogSectionKind) => void;
}

/**
 * 移動メニューの項目の文言（保留だけ「保留にする」）。
 * @graph-connects none
 */
function menuLabelKey(kind: ProductBacklogSectionKind): string {
  return kind === 'hold' ? 'productBacklog.card.menuHold' : 'productBacklog.card.menuTo';
}

/**
 * 札の「…」の移動メニュー（置き先の区分の末尾に入る）。外を押すか Esc で閉じる。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件の札の移動メニューで、付け替えできる区分を選ばせ、選んだ区分の末尾へ動かす
 * @graph-connects menuLabelKey [calls] 項目の文言
 */
function CardMenu({
  targets,
  onMove,
}: {
  targets: readonly ProductBacklogSectionKind[];
  onMove: (to: ProductBacklogSectionKind) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent) => {
      if (event.target instanceof Node && root.current?.contains(event.target) === true) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => {
      document.removeEventListener('mousedown', close);
    };
  }, [open]);
  return (
    <div
      ref={root}
      className="relative hidden flex-none md:block"
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      <button
        type="button"
        aria-label={t('productBacklog.card.menu')}
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current);
        }}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--color-text-muted)] hover:bg-[var(--color-bg-active)] hover:text-[var(--color-text-primary)]"
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-8 z-30 min-w-[200px] rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-1 shadow-lg"
        >
          <div className="px-2.5 pb-1 pt-1.5 text-[11px] text-[var(--color-text-muted)]">
            {t('productBacklog.card.menuHint')}
          </div>
          {targets.map((kind) => (
            <button
              key={kind}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onMove(kind);
              }}
              className="block w-full rounded-md px-2.5 py-1.5 text-left text-[13px] hover:bg-[var(--color-bg-hover)]"
            >
              {t(menuLabelKey(kind), { section: t(`productBacklog.sections.${kind}.name`) })}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * 案件の札1枚。1行目に区分の中の順位・題名・キー（小さく）、2行目に Backlog の状態と PL。
 * 題名が空ならキー、キーも無ければ行番号を見出しにする。focused はキーで開いた札（色を付ける）。
 * arrange を渡すと、つかむ印（ドラッグ）・動かせない理由・「…」の移動メニュー・下書きの印を出す。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件一覧の札1枚に、区分の中の順位・題名・Backlog キー・Backlog の状態・PL を出し、動かす人にはつかむ印・動かせない理由・移動メニュー・下書きで動かした印を出す
 * @graph-connects backlogCellOf [calls] Backlog の欄
 * @graph-connects CardMenu [calls] 移動メニュー
 */
export function ProductBacklogCard({
  card,
  backlogStatus,
  focused,
  arrange,
}: {
  card: Card;
  backlogStatus: 'ok' | 'unavailable';
  focused: boolean;
  arrange?: CardArrangeProps;
}) {
  const { t } = useTranslation();
  const heading =
    card.title ?? card.key ?? t('productBacklog.rowOnly', { row: String(card.rowNumber) });
  const cell = backlogCellOf(card, backlogStatus);
  const ref = useRef<HTMLDivElement>(null);
  const flash = arrange?.flash === true;
  const mark = arrange?.mark ?? null;
  useEffect(() => {
    if (flash) ref.current?.scrollIntoView({ block: 'center' });
  }, [flash]);

  const dragOver = (event: DragEvent<HTMLDivElement>) => {
    if (arrange === undefined || !arrange.acceptsDrop) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    arrange.onDragOver(event.clientY - rect.top > rect.height / 2 ? 'after' : 'before');
  };
  const drop = (event: DragEvent<HTMLDivElement>) => {
    if (arrange === undefined || !arrange.acceptsDrop) return;
    event.preventDefault();
    event.stopPropagation();
    arrange.onDrop();
  };

  const tone = [
    focused
      ? 'bg-[var(--color-accent-muted)] shadow-[inset_3px_0_0_var(--color-accent-primary)]'
      : '',
    mark !== null ? 'bg-amber-500/5' : '',
    arrange?.dropPosition === 'before' ? 'shadow-[inset_0_3px_0_var(--color-accent-primary)]' : '',
    arrange?.dropPosition === 'after' ? 'shadow-[inset_0_-3px_0_var(--color-accent-primary)]' : '',
    flash ? 'animate-pulse' : '',
  ].join(' ');

  return (
    <div
      ref={ref}
      data-key={card.key ?? undefined}
      data-row={card.rowNumber}
      aria-current={focused ? 'true' : undefined}
      draggable={arrange?.movable === true}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(card.rowNumber));
        arrange?.onDragStart();
      }}
      onDragEnd={() => arrange?.onDragEnd()}
      onDragOver={dragOver}
      onDrop={drop}
      className={`relative flex items-start gap-2.5 border-b border-[var(--color-border)] px-3.5 py-2.5 last:border-b-0 ${tone}`}
    >
      {mark !== null && (
        <span className="absolute right-12 top-0 z-[2] -translate-y-1/2 whitespace-nowrap rounded border border-amber-500/40 bg-amber-50 px-1.5 text-[11px] font-semibold leading-[17px] text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
          {mark}
        </span>
      )}
      {arrange?.movable === true && (
        <span
          aria-label={t('productBacklog.card.grip')}
          title={t('productBacklog.card.grip')}
          className="hidden flex-none cursor-grab items-center pt-0.5 text-[var(--color-text-muted)] active:cursor-grabbing md:flex"
        >
          <GripVertical className="h-4 w-4" aria-hidden="true" />
        </span>
      )}
      {arrange !== undefined && !arrange.movable && arrange.lockReason !== null && (
        <span
          title={t('productBacklog.card.locked', {
            reason: t(`productBacklog.card.lockReason.${arrange.lockReason}`),
          })}
          aria-label={t('productBacklog.card.locked', {
            reason: t(`productBacklog.card.lockReason.${arrange.lockReason}`),
          })}
          className="hidden flex-none pt-0.5 text-[var(--color-text-disabled)] md:flex"
        >
          <Lock className="h-4 w-4" aria-hidden="true" />
        </span>
      )}
      <span className="w-[22px] flex-none pt-0.5 text-right text-xs font-semibold tabular-nums text-[var(--color-text-muted)]">
        {card.rank}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <div className="flex min-w-0 items-baseline gap-2.5">
          <span
            className="min-w-0 flex-1 truncate text-sm font-semibold text-[var(--color-text-primary)]"
            title={heading}
          >
            {heading}
          </span>
          {card.key !== null && card.title !== null && (
            <span className="whitespace-nowrap text-[11px] font-medium text-[var(--color-text-muted)]">
              {card.key}
            </span>
          )}
        </div>
        <div className="flex min-w-0 items-center gap-2 text-[12.5px]">
          {cell.kind === 'values' ? (
            <>
              <Badge>{cell.status ?? t('productBacklog.none')}</Badge>
              <span className="ml-auto flex-none text-xs text-[var(--color-text-muted)]">
                {t('productBacklog.pl', { name: cell.pl ?? t('productBacklog.none') })}
              </span>
            </>
          ) : (
            <span className="text-[var(--color-text-muted)]">
              {cell.kind === 'notFound'
                ? t('productBacklog.backlogNotFound')
                : cell.kind === 'noKey'
                  ? t('productBacklog.noKey')
                  : t('productBacklog.none')}
            </span>
          )}
        </div>
      </div>
      {arrange !== undefined && arrange.movable && arrange.menuTargets.length > 0 && (
        <CardMenu targets={arrange.menuTargets} onMove={arrange.onMenuMove} />
      )}
    </div>
  );
}
